import { type KeyboardEventHandler, useImperativeHandle, useRef } from 'react';

import { VerticalCrosshair } from '../crosshair/VerticalCrosshair.js';
import { SELECTION_EDGE_THRESHOLD } from '../interactions-overlay/constants.js';
import { ZOOM_AREA_STYLES } from '../overlays/constants.js';
import { useSelectionAreaPathRef } from './hooks/use-selection-area-path-ref.js';

interface ZoomSelectionAreaProps {
  bounds: DOMRect;
  focusable: boolean;
  interactable: boolean;
  bottomFormatter?: (value: number) => string;
  topFormatter?: (value: number) => string;
  startX: number;
  endX: number;
  height: number;
  width: number;
  handleSelectionAreaKeyDown: KeyboardEventHandler;
  handleSelectionAreaStartKeyDown: KeyboardEventHandler;
  handleSelectionAreaEndKeyDown: KeyboardEventHandler;
  isBottomLabelRotated?: boolean;
  isTopLabelRotated?: boolean;
}

export const ZoomSelection = ({
  startX,
  endX,
  height,
  width,
  focusable,
  interactable,
  bottomFormatter,
  topFormatter,
  bounds,
  handleSelectionAreaKeyDown,
  handleSelectionAreaStartKeyDown,
  handleSelectionAreaEndKeyDown,
  isBottomLabelRotated = false,
  isTopLabelRotated = false,
}: ZoomSelectionAreaProps) => {
  const areaStyles = ZOOM_AREA_STYLES;
  const selectionAreaRef = useRef<SVGRectElement>(null);
  const forwardedRef = useSelectionAreaPathRef();
  useImperativeHandle(
    forwardedRef,
    () => ({
      element: selectionAreaRef.current,
      focus() {
        selectionAreaRef.current?.focus();
      },
    }),
    [],
  );

  return (
    <>
      {/*Selection area fill*/}
      <rect
        ref={selectionAreaRef}
        x={startX}
        y={0}
        width={endX - startX}
        height={height}
        tabIndex={focusable ? 0 : -1}
        onKeyDown={handleSelectionAreaKeyDown}
        {...areaStyles}
        rx="0"
      />
      {/* Transparent dragging area start */}
      <rect
        x={startX - SELECTION_EDGE_THRESHOLD}
        y={0}
        width={SELECTION_EDGE_THRESHOLD * 5}
        height={height}
        fill={'transparent'}
        onMouseDown={() => false}
        style={{
          cursor: 'col-resize',
          pointerEvents: interactable ? 'all' : 'none',
        }}
      />
      <VerticalCrosshair
        data-testid="vertical-crosshair-brush-start"
        x={startX}
        bottomLabel={bottomFormatter ? bottomFormatter(startX) : undefined}
        topLabel={topFormatter ? topFormatter(startX) : undefined}
        height={height}
        dashed={false}
        focusable={focusable}
        chartBounds={bounds}
        onKeyDown={handleSelectionAreaStartKeyDown}
        variant="zoom"
        isTopLabelRotated={isTopLabelRotated}
        isBottomLabelRotated={isBottomLabelRotated}
      />
      {/* Transparent dragging area end */}
      <rect
        x={endX - SELECTION_EDGE_THRESHOLD}
        y={0}
        width={SELECTION_EDGE_THRESHOLD * 5}
        height={height}
        fill={'transparent'}
        style={{
          cursor: 'col-resize',
          pointerEvents: interactable ? 'all' : 'none',
        }}
      />
      <VerticalCrosshair
        data-testid="vertical-crosshair-brush-end"
        x={endX}
        bottomLabel={bottomFormatter ? bottomFormatter(endX) : undefined}
        topLabel={topFormatter ? topFormatter(endX) : undefined}
        height={height}
        dashed={false}
        focusable={focusable}
        chartBounds={bounds}
        onKeyDown={handleSelectionAreaEndKeyDown}
        variant="zoom"
        isTopLabelRotated={isTopLabelRotated}
        isBottomLabelRotated={isBottomLabelRotated}
      />
    </>
  );
};
